"use client";

import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  label: string;
  value: number;
  suffix: string;
  unit: string;
  descriptor: string;
};
const stats: Stat[] = [{
  label: "CLIENT RETENTION",
  value: 98,
  suffix: "%",
  unit: "RATE",
  descriptor: "Building enduring partnerships through consistent delivery and transparent communication."
}, {
  label: "PROJECT SUCCESS",
  value: 150,
  suffix: "+",
  unit: "COMPLETED",
  descriptor: "Tailored project management solutions that drive efficiency and measurable growth."
}, {
  label: "RESPONSE TIME",
  value: 2,
  suffix: "",
  unit: "HOURS",
  descriptor: "Rapid, dedicated support ensuring your business operations never face unnecessary downtime."
}, {
  label: "YEARS OF SERVICE",
  value: 10,
  suffix: "+",
  unit: "YEARS",
  descriptor: "A decade of refining our expertise to provide innovative, high-impact professional services."
}];
function CountUp({
  value,
  isVisible,
  suffix
}: {
  value: number;
  isVisible: boolean;
  suffix: string;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 1800;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      if (progress < 1) raf = requestAnimationFrame(tick);else setCount(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible, value]);
  return <span>
      {count}
      {suffix}
    </span>;
}
export default function Results() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="4591" id="measurable-results" className="relative overflow-hidden bg-background text-foreground py-32 md:py-48 lg:py-[200px]">
      {}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "repeating-radial-gradient(ellipse at 30% 20%, var(--foreground) 0px, transparent 2px, transparent 18px), repeating-radial-gradient(ellipse at 70% 80%, var(--foreground) 0px, transparent 2px, transparent 22px), repeating-linear-gradient(115deg, var(--foreground) 0px, transparent 1px, transparent 24px)"
    }} />

      <div ref={ref} className="relative mx-auto max-w-7xl px-6">
        {}
        <h2 className="font-default font-bold uppercase text-center leading-[0.95] tracking-tight text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7rem]">
          <span className="block">Measurable</span>
          <span className="block">
            Impact{" "}
            <span className="relative inline-block">
              Delivered
              <span className="text-primary" style={{
              textShadow: "0 0 12px var(--primary), 0 0 32px var(--primary)"
            }}>
                .
              </span>
            </span>
          </span>
        </h2>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-border">
          {stats.map((stat, i) => <div data-index={i} key={stat.label} className={`relative flex flex-col justify-between min-h-[280px] py-10 px-6 lg:px-8 border-b border-border ${i < stats.length - 1 ? "lg:border-r lg:border-border" : ""} ${i % 2 === 0 ? "sm:border-r sm:border-border lg:border-r" : ""}`}>
              {}
              <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {stat.label}
              </div>

              {}
              <div className="flex items-baseline gap-3 my-6">
                <span className="font-mono font-bold text-foreground leading-none" style={{
              fontSize: "clamp(4rem, 8vw, 7rem)"
            }}>
                  <CountUp value={stat.value} isVisible={isVisible} suffix={stat.suffix} />
                </span>
                <span className="font-default font-semibold text-sm sm:text-base uppercase tracking-wider text-muted-foreground">
                  {stat.unit}
                </span>
              </div>

              {}
              <div className="h-[2px] w-10 mb-5" style={{
            backgroundColor: "var(--primary)",
            boxShadow: "0 0 8px var(--primary), 0 0 16px var(--primary)"
          }} />

              {}
              <p className="font-default text-base sm:text-lg leading-snug text-foreground/85 max-w-xs">
                {stat.descriptor}
              </p>
            </div>)}
        </div>

        {}
        <div className="mt-20 md:mt-24 flex items-center justify-center gap-3 text-center">
          <span className="inline-flex items-center justify-center h-7 w-7 border border-border" style={{
          color: "var(--primary)",
          boxShadow: "0 0 10px color-mix(in oklab, var(--primary) 60%, transparent)"
        }} aria-hidden>
            <ShieldCheck className="h-4 w-4" />
          </span>
          <p className="font-default text-lg sm:text-xl md:text-2xl text-foreground/90">
            Verified performance metrics for every client engagement.
          </p>
        </div>
      </div>
    </section>;
}